import { getRandomUUID } from 'shared/lib/core/lib/utils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { CustomError, thunkAxiosRequest } from 'shared/api';
import {
  CalculateDepositeData,
  CalculateDepositeResponce,
	Deposit,
	ExcelResponce,
	Filter,
	Order,
	OrderBookingData,
	OrderBookingDataPatch,
	OrdersByDateTimeParams,
	OrdersResponce,
	PaymentProvider,
	PostOrderResponce,
	Status,
} from './types';

export const getOrdersNext = createAsyncThunk<
	Order[],
	unknown,
	{ rejectValue: CustomError }
>('getOrdersNext', async (_, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/orders/next`,
		rejectWithValue,
	});
});

export const getOrdersByDateTime = createAsyncThunk<
	OrdersResponce,
	OrdersByDateTimeParams
>('getOrdersByDateTime', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/orders`,
		params,
		rejectWithValue,
	});
});

export const getOrdersByFilter = createAsyncThunk<OrdersResponce, Filter>(
	'getOrdersByFilter',
	async (params, { rejectWithValue }) => {
		return await thunkAxiosRequest({
			path: `/api/v1/orders`,
			abortWithParams: true,
			params,
			rejectWithValue,
		});
	},
	{
		getPendingMeta: params => params,
	},
);

export const getOrdersByDate = createAsyncThunk<OrdersResponce, { dt_start__gte: string; dt_start__lte: string; status: string }>(
	'getOrdersByDate',
	async (params, { rejectWithValue }) => {
		return await thunkAxiosRequest({
			path: `/api/v1/orders`,
			abortWithParams: true,
			params,
			rejectWithValue,
		});
	},
	{
		getPendingMeta: params => params,
	},
);

export const getOrdersByVisit = createAsyncThunk<
	OrdersResponce,
	unknown,
	{ rejectValue: CustomError }
>('getOrdersByVisit', async (_, { rejectWithValue }) => {
	const params = { status: 'visit_started' };
	return await thunkAxiosRequest({
		path: `/api/v1/orders`,
		params,
		rejectWithValue,
	});
});

export const getOrdersByTable = createAsyncThunk<Order[], { on_date?: string; status?: Status, resource: string; }>(
	'getOrdersByTable',
	async (params, { rejectWithValue }) => {
		const filter: { on_date?: string; status?: Status } = {};

		if (params.on_date) {
			filter.on_date = params.on_date;
		}

		if (params.on_date) {
			filter.status = params.status;
		}

		return await thunkAxiosRequest({
			path: `/api/v1/resources/${params.resource}/orders`,
			rejectWithValue,
			params: filter,
			abortWithParams: true,
		});
	},
	{
		getPendingMeta: params => params,
	},
);

export const getOrder = createAsyncThunk<
	Order,
	{ id: number },
	{ rejectValue: CustomError }
>(
	'getOrder',
	async (params, { rejectWithValue }) => {
		return await thunkAxiosRequest({
			path: `/api/v1/orders/${params.id}`,
			rejectWithValue,
		});
	},
	{
		getPendingMeta: params => params,
	},
);

export const getOrderInfo = createAsyncThunk<
	Order,
	{ id: number },
	{ rejectValue: CustomError }
>(
	'getOrderInfo',
	async (params, { rejectWithValue }) => {
		return await thunkAxiosRequest({
			path: `/api/v1/orders/${params.id}`,
			rejectWithValue,
		});
	},
	{
		getPendingMeta: params => params,
	},
);

export const toStartVisitForOrder = createAsyncThunk<
	unknown,
	{ id: number },
	{ rejectValue: CustomError }
>('toStartVisitForOrder', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		method: 'post',
		path: `/api/v1/orders/${params.id}/status/start_visit`,
		rejectWithValue,
	});
});

export const toEndVisitOrder = createAsyncThunk<
	unknown,
	{ id: number },
	{ rejectValue: CustomError }
>('toEndVisitOrder', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		method: 'post',
		path: `/api/v1/orders/${params.id}/status/end_visit`,
		rejectWithValue,
	});
});

export const toCancelBooking = createAsyncThunk<
	unknown,
	{ id: number },
	{ rejectValue: CustomError }
>('toCancelBooking', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		method: 'post',
		path: `/api/v1/orders/${params.id}/status/cancel`,
		params: { status: 'cancelled' },
		rejectWithValue,
	});
});

export const toWaitingList = createAsyncThunk<
	unknown,
	{ id: number },
	{ rejectValue: CustomError }
>('toWaitingList', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		method: 'post',
		path: `/api/v1/orders/${params.id}/status/to_waiting_list`,
		rejectWithValue,
	});
});

export const createOrder = createAsyncThunk<
	Order,
	OrderBookingData,
	{ rejectValue: CustomError }
>('createOrder', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		method: 'post',
		path: `/api/v1/orders`,
		params: { ...params },
		rejectWithValue,
	});
});

export const editOrder = createAsyncThunk<
	PostOrderResponce,
	OrderBookingDataPatch,
	{ rejectValue: CustomError }
>('editOrder', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		method: 'patch',
		path: `/api/v1/orders/${params.id}`,
		params: params.body,
		rejectWithValue,
	});
});

export const editOrderConfirmed = createAsyncThunk<
	PostOrderResponce,
	{ is_confirmed: boolean | null; id?: number; },
	{ rejectValue: CustomError }
>('editOrder', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		method: 'patch',
		path: `/api/v1/orders/${params.id}`,
		params: { is_confirmed: params.is_confirmed },
		rejectWithValue,
	});
});

export const toBook = createAsyncThunk<
	PostOrderResponce,
	OrderBookingData,
	{ rejectValue: CustomError }
>('toBook', async (params, { rejectWithValue }) => {
	const cryptoKey = params.idempotencyKey || (crypto?.randomUUID ? crypto.randomUUID() : getRandomUUID());
	delete params.idempotencyKey;

	return await thunkAxiosRequest({
		method: 'post',
		path: `/api/v1/orders/book`,
		params: { ...params },
		rejectWithValue,
		headers: { 'X-Idempotency-Key': cryptoKey },
		skipOfflineError: false,
	});
});

export const editBooking = createAsyncThunk<
	PostOrderResponce,
	OrderBookingData,
	{ rejectValue: CustomError }
>('editBooking', async (params, { rejectWithValue }) => {
	const cryptoKey = params.idempotencyKey || (crypto?.randomUUID ? crypto.randomUUID() : getRandomUUID());
	delete params.idempotencyKey;

	return await thunkAxiosRequest({
		path: `/api/v1/orders/book`,
		params: { ...params },
		method: 'put',
		rejectWithValue,
		headers: { 'X-Idempotency-Key': cryptoKey },
		skipOfflineError: false,
	});
});

export const getWaitingOrders = createAsyncThunk<OrdersResponce, Filter>(
	'waitingOrders',
	async (params, { rejectWithValue }) => {
		const filterParams: Filter = {
			...params,
			status: ['waiting_list'],
			ordering: 'dt_start',
		};

		return await thunkAxiosRequest({
			path: `/api/v1/orders`,
			params: filterParams,
			abortWithParams: true,
			rejectWithValue,
		});
	},
);

export const getOrdersByPerson = createAsyncThunk<
	OrdersResponce,
	{ person: number; ordering: string },
	{ rejectValue: CustomError }
>(
	'getOrdersByPerson',
	async (params, { rejectWithValue }) => {
		return await thunkAxiosRequest({
			path: `/api/v1/orders`,
			params,
			rejectWithValue,
		});
	},
	{
		getPendingMeta: params => params,
	},
);

export const restoreOrderToVisit = createAsyncThunk<
	OrdersResponce,
	{ id: number },
	{ rejectValue: CustomError }
>('restoreOrderToVisit', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/orders/${params.id}/status/restore_to_visit`,
		method: 'post',
		rejectWithValue,
	});
});

export const restoreOrderToAppointment = createAsyncThunk<
	OrdersResponce,
	{ id: number },
	{ rejectValue: CustomError }
>('restoreOrderToAppoinment', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/orders/${params.id}/status/restore_to_appointment`,
		method: 'post',
		rejectWithValue,
	});
});

export const markOrderVisitAsMissed = createAsyncThunk<
	OrdersResponce,
	{ id: number },
	{ rejectValue: CustomError }
>('markOrderVisitAsMissed', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/orders/${params.id}/status/mark_visit_as_missed`,
		method: 'post',
		rejectWithValue,
	});
});

export const calculateDeposite = createAsyncThunk<
  CalculateDepositeResponce,
	CalculateDepositeData,
	{ rejectValue: CustomError }
>('calculateDeposite', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/depositrules/calculate`,
    params,
		method: 'post',
		rejectWithValue,
	});
});

export const getPaymentProviders = createAsyncThunk<
  PaymentProvider[],
  unknown,
	{ rejectValue: CustomError }
>('getPaymentProviders', async (_,{ rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/paymentproviders`,
		rejectWithValue,
	});
});

export const getDepositStatusDoneAllow = createAsyncThunk<
  PaymentProvider[],
  { id:number },
	{ rejectValue: CustomError }
>('getDepositStatusDoneAllow', async (params,{ rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/deposits/${params.id}/status/done`,
		rejectWithValue,
	});
});

export const setDepositStatusDone = createAsyncThunk<
  Deposit,
  { id:number },
	{ rejectValue: CustomError }
>('setDepositStatusDone', async (params,{ rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/deposits/${params.id}/status/done`,
    method: 'post',
		rejectWithValue,
	});
});

export const getDepositStatusRejectAllow = createAsyncThunk<
  PaymentProvider[],
  { id:number },
	{ rejectValue: CustomError }
>('getDepositStatusRejectAllow', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/deposits/${params.id}/status/reject`,
		rejectWithValue,
	});
});

export const getDepositStatusRefundAllow = createAsyncThunk<
  unknown,
  { id:number },
	{ rejectValue: CustomError }
>('getDepositStatusRefundAllow', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/deposits/${params.id}/status/refund`,
		rejectWithValue,
	});
});

export const setDepositStatusReject = createAsyncThunk<
  Deposit,
  { id:number },
	{ rejectValue: CustomError }
>('setDepositStatusReject', async (params,{ rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/deposits/${params.id}/status/reject`,
    method: 'post',
		rejectWithValue,
	});
});

export const setDepositStatusRefund = createAsyncThunk<
  Deposit,
  { id:number },
	{ rejectValue: CustomError }
>('setDepositStatusRefund', async (params,{ rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/deposits/${params.id}/status/refund`,
    method: 'post',
		rejectWithValue,
	});
});

export const setDepositStatusRevert = createAsyncThunk<
  Deposit,
  { id:number },
	{ rejectValue: CustomError }
>('setDepositStatusRefund', async (params,{ rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/deposits/${params.id}/status/revert`,
    method: 'post',
		rejectWithValue,
	});
});

export const deleteDeposit = createAsyncThunk<
  Deposit,
  { id:number },
	{ rejectValue: CustomError }
>('deleteDeposit', async (params,{ rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/deposits/${params.id}`,
    method: 'delete',
		rejectWithValue,
	});
});

export const getDeposit = createAsyncThunk<
  Deposit,
  { id:number },
	{ rejectValue: CustomError }
>('getDeposit', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/deposits/${params.id}`,
		rejectWithValue,
	});
});

export const updateDepositPaymentUrl = createAsyncThunk<
  unknown,
  { id:number },
	{ rejectValue: CustomError }
>('updateDepositPaymentUrl', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/deposits/${params.id}/update_payment_url`,
		rejectWithValue,
	});
});

export const patchDeposit = createAsyncThunk<
  unknown,
  { id:number, payload: Partial<Deposit> },
	{ rejectValue: CustomError }
>('patchDeposit', async (params, { rejectWithValue }) => {
	return await thunkAxiosRequest({
		path: `/api/v1/deposits/${params.id}`,
    method: 'patch',
    params: params.payload,
		rejectWithValue,
	});
});

export const getExcelOrders = createAsyncThunk<ExcelResponce, Filter>(
	'getExcelOrders',
	async (params, { rejectWithValue }) => {
    const cryptoKey = params.idempotencyKey || (crypto?.randomUUID ? crypto.randomUUID() : getRandomUUID());
    delete params.idempotencyKey;

    return await thunkAxiosRequest({
      method: 'post',
      path: `/api/v1/orders/export`,
      params: { ...params },
      rejectWithValue,
      headers: { 'X-Idempotency-Key': cryptoKey },
      skipOfflineError: false,
    });
	},
);
